import React, { useState, useEffect, useCallback } from 'react';
import { Cpu, Plus, Pencil, Trash2, ToggleLeft, ToggleRight, X, Check } from 'lucide-react';
import { apiService } from '../services/api';
import type { ModelConfig, ProviderKey } from '../types';

function Badge({ active }: { active: boolean }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
      {active ? 'Active' : 'Disabled'}
    </span>
  );
}

interface EditModalProps {
  model: Partial<ModelConfig>;
  availableProviders: string[];
  onSave: (data: Partial<ModelConfig>) => Promise<void>;
  onClose: () => void;
}

function ModelModal({ model, availableProviders, onSave, onClose }: EditModalProps) {
  const [form, setForm] = useState<Partial<ModelConfig>>({ active: true, tier: ['premium'], contextWindow: 128000, inputPer1kUsd: 0.00015, outputPer1kUsd: 0.0006, ...model });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(form); onClose(); } finally { setSaving(false); }
  };

  const field = (key: keyof ModelConfig, label: string, type = 'text') => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type={type}
        value={String(form[key] ?? '')}
        onChange={e => setForm(f => ({ ...f, [key]: type === 'number' ? Number(e.target.value) : e.target.value }))}
        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{model.id ? 'Edit Model' : 'New Model Config'}</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        {!model.id && field('id', 'Model ID (e.g. gpt-5.4-mini)')}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Provider
            {availableProviders.length === 0 && (
              <span className="ml-2 text-amber-500 font-normal">— no API keys configured yet</span>
            )}
          </label>
          {availableProviders.length > 0 ? (
            <select
              value={form.provider ?? ''}
              onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— select provider —</option>
              {availableProviders.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
              {!availableProviders.includes('self-hosted') && (
                <option value="self-hosted">self-hosted</option>
              )}
            </select>
          ) : (
            <input
              type="text"
              value={String(form.provider ?? '')}
              onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
              placeholder="e.g. openai"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          )}
        </div>
        {field('endpoint', 'Endpoint URL (optional)')}
        {field('contextWindow', 'Context Window (tokens)', 'number')}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Input price / 1M tokens (USD)</label>
          <input
            type="number"
            value={parseFloat(((form.inputPer1kUsd ?? 0) * 1000).toPrecision(8))}
            onChange={e => setForm(f => ({ ...f, inputPer1kUsd: Number(e.target.value) / 1000 }))}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Output price / 1M tokens (USD)</label>
          <input
            type="number"
            value={parseFloat(((form.outputPer1kUsd ?? 0) * 1000).toPrecision(8))}
            onChange={e => setForm(f => ({ ...f, outputPer1kUsd: Number(e.target.value) / 1000 }))}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tiers (comma-separated)</label>
          <input
            type="text"
            value={(form.tier ?? []).join(', ')}
            onChange={e => setForm(f => ({ ...f, tier: e.target.value.split(',').map(t => t.trim()).filter(Boolean) }))}
            className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <span className="text-sm text-gray-700">Active</span>
          <input type="checkbox" checked={!!form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="sr-only" />
          {form.active ? <ToggleRight size={24} className="text-indigo-600" /> : <ToggleLeft size={24} className="text-gray-400" />}
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Models() {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [providerKeys, setProviderKeys] = useState<ProviderKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ mode: 'create' | 'edit'; model: Partial<ModelConfig> } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  // Providers that have at least one active key configured — shown in the Add Model dropdown
  const configuredProviders = Array.from(
    new Set(providerKeys.filter(k => k.status === 'active').map(k => k.provider))
  ).sort();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, pk] = await Promise.all([apiService.listModels(), apiService.listProviderKeys()]);
      setModels(m);
      setProviderKeys(pk);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async (data: Partial<ModelConfig>) => {
    if (modal?.mode === 'create') await apiService.createModel(data);
    else await apiService.updateModel(data.id!, data);
    await load();
  };

  const toggleActive = async (m: ModelConfig) => {
    await apiService.updateModel(m.id, { active: !m.active });
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(`Delete model config "${id}"? This cannot be undone.`)) return;
    setDeleting(id);
    try { await apiService.deleteModel(id); await load(); } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Models</h1>
          <p className="mt-1 text-sm text-gray-500">Manage model configs, pricing, and kill-switches</p>
        </div>
        <button
          onClick={() => setModal({ mode: 'create', model: {} })}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus size={15} /> Add Model
        </button>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading...</div>
        ) : (
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Model ID', 'Provider', 'Tiers', 'Context', 'In $/1M', 'Out $/1M', 'Status', ''].map(h => (
                  <th key={h} className="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {models.map(m => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="py-3 px-4 font-mono text-sm text-gray-800">{m.id}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">{m.provider}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1 flex-wrap">
                      {(m.tier ?? []).map(t => (
                        <span key={t} className="px-1.5 py-0.5 text-xs bg-blue-50 text-blue-700 rounded">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600">{(m.contextWindow ?? 0).toLocaleString()}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">${parseFloat(((m.inputPer1kUsd ?? 0) * 1000).toPrecision(8))}</td>
                  <td className="py-3 px-4 text-sm text-gray-600">${parseFloat(((m.outputPer1kUsd ?? 0) * 1000).toPrecision(8))}</td>
                  <td className="py-3 px-4"><Badge active={m.active} /></td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleActive(m)} title={m.active ? 'Disable' : 'Enable'} className="text-gray-400 hover:text-indigo-600">
                        {m.active ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                      </button>
                      <button onClick={() => setModal({ mode: 'edit', model: m })} className="text-gray-400 hover:text-blue-600">
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(m.id)}
                        disabled={deleting === m.id}
                        className="text-gray-400 hover:text-red-600 disabled:opacity-30"
                      >
                        {deleting === m.id ? <Check size={15} className="animate-spin" /> : <Trash2 size={15} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {models.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-sm text-gray-400">No model configs yet</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {modal && <ModelModal model={modal.model} availableProviders={configuredProviders} onSave={handleSave} onClose={() => setModal(null)} />}
    </div>
  );
}
